import { useEffect, useMemo, useRef } from "react";
import {
  Control,
  useController,
  useFormContext,
  useWatch
} from "react-hook-form";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { orange } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";

import CheckboxFormField from "components/checkbox-form-field";
import { ROOM_TYPE_ENTRY_ROOM, ROOM_TYPE_EXIT_ROOM } from "constants/rooms";
import useRoomValidation from "features/scheduling/hooks/use-appointments-room.hook";
import { useGetClinicRoomsQuery } from "services/rooms";

import { AppointmentFormValues } from "../appointment-form.component";

type RoomFieldProps = {
  control: Control<AppointmentFormValues, object>;
  showIcon?: boolean;
  sx?: SxProps;
};

export default function RoomField({
  control,
  sx,
  showIcon = false
}: RoomFieldProps) {
  const {
    setValue,
    formState: { dirtyFields }
  } = useFormContext<AppointmentFormValues>();
  const [
    clinic,
    room,
    appointmentDate,
    startTime,
    endTime,
    appointmentId,
    patient,
    title,
    showRoomWarning
  ] = useWatch({
    control,
    name: [
      "clinic",
      "room",
      "appointment_date",
      "start_time",
      "end_time",
      "id",
      "patient",
      "title",
      "show_room_warning"
    ]
  });

  const {
    field: { onChange, ...restField }
  } = useController({
    name: "room",
    control
  });

  const { isRoomAvailable } = useRoomValidation();

  const { data, isLoading } = useGetClinicRoomsQuery(clinic?.id);

  const rooms = useMemo(
    () =>
      (data || []).filter(
        (room) =>
          room.type !== ROOM_TYPE_ENTRY_ROOM &&
          room.type !== ROOM_TYPE_EXIT_ROOM
      ),
    [data]
  );

  const anyRelevantFieldDirty = useMemo(
    () =>
      [
        dirtyFields.room,
        dirtyFields.clinic,
        dirtyFields.appointment_date,
        dirtyFields.start_time,
        dirtyFields.end_time
      ].some(Boolean),
    [
      dirtyFields.room,
      dirtyFields.clinic,
      dirtyFields.appointment_date,
      dirtyFields.start_time,
      dirtyFields.end_time
    ]
  );

  const prevRoomDataRef = useRef<any>({});

  useEffect(() => {
    const currentRoomData = {
      room: room?.id,
      clinic: clinic?.id,
      appointmentDate: appointmentDate?.toString(),
      startTime: startTime?.toString(),
      endTime: endTime?.toString(),
      title,
      patientId: patient?.id,
      appointmentId
    };

    const hasRoomDataChanged = Object.keys(currentRoomData).some(
      (key) => prevRoomDataRef.current[key] !== (currentRoomData as any)[key]
    );

    if (
      !hasRoomDataChanged &&
      Object.keys(prevRoomDataRef.current).length > 0
    ) {
      return;
    }

    prevRoomDataRef.current = currentRoomData;
    const checkRoomWarning = async () => {
      if (
        !room ||
        !clinic ||
        !appointmentDate ||
        !startTime ||
        !endTime ||
        isLoading ||
        !anyRelevantFieldDirty
      ) {
        return;
      }

      const isAvailable = await isRoomAvailable(
        room,
        new Date(appointmentDate),
        new Date(startTime),
        new Date(endTime),
        title,
        patient,
        clinic,
        appointmentId || undefined
      );

      const shouldShowWarning = !isAvailable;
      setValue("show_room_warning", shouldShowWarning, {
        shouldDirty: true,
        shouldValidate: true
      });
      setValue("allow_room_conflict", false, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    checkRoomWarning();
  }, [
    room,
    clinic,
    appointmentDate,
    startTime,
    endTime,
    isLoading,
    isRoomAvailable,
    title,
    patient,
    appointmentId,
    setValue,
    anyRelevantFieldDirty
  ]);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={sx}
      >
        {showIcon && (
          <LocationOnOutlinedIcon sx={{ mt: 1.5, mr: 1.5 }} color="action" />
        )}
        <Autocomplete
          disablePortal
          options={rooms || []}
          loading={isLoading}
          fullWidth
          popupIcon={<ExpandMoreOutlinedIcon />}
          getOptionLabel={({ name }) => name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          {...restField}
          renderInput={(params) => <TextField {...params} label="Room" />}
          onChange={(_, data) => onChange(data)}
        />
      </Box>

      {showRoomWarning && (
        <Stack direction="row">
          <Box sx={{ pl: 4, pt: 1.5, color: orange[900] }}>
            <Stack direction="row" spacing={0.25}>
              <WarningAmberIcon sx={{ height: 18 }} />
              <Typography>This room is reserved at this time.</Typography>
            </Stack>
          </Box>
          <FormControlLabel
            label="Ignore Room Reservation?"
            sx={{ pl: 3 }}
            control={
              <CheckboxFormField
                testId="allow_room_conflict"
                name="allow_room_conflict"
                control={control}
              />
            }
          />
        </Stack>
      )}
    </>
  );
}
